import { Capacitor, CapacitorCookies } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';
import { InAppBrowser, BackgroundColor } from '@capgo/inappbrowser';
import { API_URL, TENANT } from '../../config/constants';

// On native (Capacitor) the webview origin is capacitor://localhost / https://localhost,
// so the API cookie is third-party: the session travels as a Bearer token instead.
// API calls go through CapacitorHttp (capacitor.config.json), so no CORS setup is needed on the API.
export const IS_NATIVE = Capacitor.isNativePlatform();

const TOKEN_KEY = 'access_token';

// kept in memory so axios can read it synchronously; loaded once at boot
let accessToken = null;

export const loadAccessToken = async () => {
    if (!IS_NATIVE) return;
    accessToken = (await Preferences.get({ key: TOKEN_KEY })).value;
};

export const getAccessToken = () => accessToken;

export const setAccessToken = (token) => {
    accessToken = token;
    return Preferences.set({ key: TOKEN_KEY, value: token });
};

// the backend still accepts the cookie as a fallback: a leftover one (SAML webview, login
// responses stored by the native http stack) would keep the session alive after logout
const clearSessionCookie = (origin = new URL(API_URL).origin) => CapacitorCookies.deleteCookie({
    // an origin, not API_URL: Android derives the cookie path from the url and the cookie lives on "/"
    url: origin, key: TOKEN_KEY,
}).catch(() => {});

export const clearAccessToken = () => {
    accessToken = null;
    clearSessionCookie();
    return Preferences.remove({ key: TOKEN_KEY });
};

// SAML runs the plain web flow in an in-app webview: the callback sets the session cookie on the
// API host and redirects to a web app page under /{tenant}/ (or /{tenant}/error_page?msg=, or
// /{tenant}/manual_access_request?token= when the backend can't match the user). The session JWT is read
// from the native cookie store, so no backend change is needed for the app.
// The webview shows up only once the first page (the university login) is loaded: meanwhile the
// login button shows its loader, instead of an empty webview.
const SAML_LOAD_TIMEOUT = 30000;
export const nativeSamlLogin = (title) => new Promise(async (resolve) => {
    let listeners = [];
    let finished = false;
    // pageLoadError can't tell a real failure from a redirect cancelling the previous load,
    // so a first page that never loads (offline, IdP down) is caught by a timeout instead
    const loadTimeout = setTimeout(() => finish({ error: 'Internal_Error' }), SAML_LOAD_TIMEOUT);
    const finish = (result) => {
        if (finished) return;
        finished = true;
        clearTimeout(loadTimeout);
        listeners.forEach(l => l.remove());
        InAppBrowser.close().catch(() => {});
        resolve(result);
    };
    listeners = await Promise.all([
        InAppBrowser.addListener('urlChangeEvent', async ({ url }) => {
            const { origin, pathname, searchParams } = new URL(url);
            if (pathname.startsWith(`/${TENANT}/error_page`)) return finish({ error: searchParams.get('msg') || 'Internal_Error' });
            // first login not matched to a graduatoria: no session, the request page is shown in the app
            if (pathname === `/${TENANT}/manual_access_request`) return finish({ manualAccessToken: searchParams.get('token') || '' });
            // any web app page counts as landing (/{tenant}/exams, /{tenant}/...): only the api paths are still mid-flow
            if (pathname !== `/${TENANT}` && !pathname.startsWith(`/${TENANT}/`)) return;
            if (pathname.startsWith(`/${TENANT}/api/`)) return;
            // the callback sets the cookie on server_name, the host it redirects to (may differ from API_URL)
            const { access_token } = await InAppBrowser.getCookies({ url: origin, includeHttpOnly: true });
            if (access_token) await setAccessToken(access_token);
            clearSessionCookie(origin);
            finish({ error: access_token ? null : 'Internal_Error' });
        }),
        InAppBrowser.addListener('browserPageLoaded', () => clearTimeout(loadTimeout)),
        InAppBrowser.addListener('closeEvent', () => finish({ cancelled: true })),
    ]);
    await InAppBrowser.openWebView({
        url: `${API_URL}/start_saml_login`, title,
        isPresentAfterPageLoad: true,
        backgroundColor: BackgroundColor.WHITE,
    });
});
